import { Flex } from '@servicetitan/anvil2';
import { SubmitApplicationDecision, LoanStatus } from '@servicetitan/financing-api-client';
import { DecisionDataStore, SecondLook } from '@servicetitan/financing-decision-shared';
import { WorkflowStore } from '@servicetitan/financing-workflow-shared';
import { LoadingScreen } from '@servicetitan/financing-utils-shared';
import { useSecondLookSubmission } from './hooks/useSecondLookSubmission';
import { useLoanStatusPolling } from './hooks/useLoanStatusPolling';

export const SecondLookSubmitLoanWorkflow = () => {
    const { decisionData, setDecisionData } = DecisionDataStore.useStore();
    const { setWorkflowStage } = WorkflowStore.useStore();

    const { loanId } = useSecondLookSubmission({
        autoSubmit: true,
    });

    useLoanStatusPolling({
        loanId,
        onDecisionChange: (decision, loanData, loanId) => {
            const decisionData = {
                decision,
                loanId,
                externalLoanId: loanData.externalId ?? '',
                planNumber: null,
                description:
                    loanData.status === LoanStatus.Approved ||
                    loanData.status === LoanStatus.Prequalified
                        ? (loanData.status ?? null)
                        : null,
                planType: null,
                approvedAmount:
                    loanData.requestedAmount != null && loanData.approvedAmount != null
                        ? Math.min(loanData.requestedAmount, loanData.approvedAmount)
                        : (loanData.requestedAmount ?? loanData.approvedAmount ?? null),
                term: null,
                rate: null,
                payment: null,
                maxCreditAmount: loanData.approvedAmount ?? null,
                declineRedirectUrl: loanData.declineRedirectUrl ?? null,
                redirectUrl: loanData.redirectUrl ?? null,
            };

            setDecisionData(decisionData);

            switch (decision) {
                case SubmitApplicationDecision.Declined:
                case SubmitApplicationDecision.Approved:
                case SubmitApplicationDecision.Submitted:
                    // Submitted here means the poller saw a redirectUrl (distinct from Review).
                    setWorkflowStage('second-look-decision');
                    break;
                case SubmitApplicationDecision.Review:
                    // Stay in current workflow for review
                    break;
                default:
                    setWorkflowStage('application');
                    break;
            }
        },
        onTimeout: loanId => {
            // Handle timeout - set decision data and go to decision stage
            setDecisionData({
                decision: SubmitApplicationDecision.Declined,
                loanId,
                externalLoanId: '',
                planNumber: null,
                description: 'Request timed out after 2 minutes',
                planType: null,
                approvedAmount: null,
                term: null,
                rate: null,
                payment: null,
                maxCreditAmount: null,
                declineRedirectUrl: null,
                redirectUrl: null,
            });
            setWorkflowStage('second-look-decision');
        },
    });

    // Show SecondLook.UnderReview component if decision is Review, otherwise show loading
    if (decisionData?.decision === SubmitApplicationDecision.Review) {
        return <SecondLook.UnderReview />;
    }

    return (
        <Flex direction="column" gap="6" alignItems="center" style={{ margin: '240px auto' }}>
            <LoadingScreen message="Processing your application..." />
        </Flex>
    );
};
